'use client';

import { useEffect, useState } from 'react';

/**
 * Barra de progreso superior (1.5px de alto) que indica cuánto se ha
 * scrolleado de la página. Aparece tras 200px de scroll para no distraer
 * en el hero. Usa transform scale para no provocar reflow.
 *
 * Respeta prefers-reduced-motion: si el usuario lo tiene activo, no se
 * muestra (el indicador es puramente visual, no funcional).
 */
export default function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    setEnabled(true);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = docHeight > 0 ? Math.min(Math.max(scrolled / docHeight, 0), 1) : 0;
        setProgress(ratio);
        ticking = false;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!enabled) return null;

  const visible = progress > 0.005;

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 inset-x-0 h-[2px] z-40 pointer-events-none"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 240ms ease',
      }}
    >
      <div
        className="h-full bg-gradient-to-r from-emerald-800 via-emerald-600 to-emerald-400 origin-left"
        style={{
          transform: `scaleX(${progress})`,
          transition: 'transform 80ms linear',
          willChange: 'transform',
        }}
      />
    </div>
  );
}
